import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  ChevronDownIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/20/solid';
import { Checkbox } from 'components/ui';
import { useClickOutside } from 'hooks';
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { createPortal } from 'react-dom';
import clsx from 'clsx';

/**
 * A premium multi-select or single-select dropdown with search.
 * Designed to be used in forms and modals.
 */
export const MultiSelectCheckboxDropdown = ({
  options = [],
  value = [], // array if isMulti=true, single value (string) if isMulti=false
  onChange,
  label,
  placeholder = "Select options",
  className = "",
  disabled = false,
  usePortal = true,
  searchPlaceholder = "Search...",
  required = false,
  error,
  allowCustom = false,
  isMulti = true,
  size = "md", // "sm" | "md"
  sortBySelected = false,
  closeOnSelect = false,
  placement = "auto", // "auto" | "bottom" | "top"
  dropdownHeader = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownRect, setDropdownRect] = useState(null);
  const [customValue, setCustomValue] = useState("");

  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useClickOutside(containerRef, (e) => {
    if (usePortal && dropdownRef.current?.contains(e.target)) return;
    setIsOpen(false);
    setSearchTerm("");
  });

  const updatePosition = () => {
    if (!inputRef.current) return;
    const wrapper = inputRef.current.closest('.trigger-wrapper');
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 300;
    const dropdownWidth = Math.max(rect.width, 280);

    const styleObj = {
      width: rect.width,
    };

    if (rect.left + dropdownWidth > window.innerWidth) {
      styleObj.right = window.innerWidth - rect.right;
    } else {
      styleObj.left = rect.left;
    }

    if (placement === "top") {
      styleObj.bottom = window.innerHeight - rect.top + 4;
    } else if (placement === "bottom") {
      styleObj.top = rect.bottom + 4;
    } else {
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        styleObj.bottom = window.innerHeight - rect.top + 4;
      } else {
        styleObj.top = rect.bottom + 4;
      }
    }

    setDropdownRect(styleObj);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = (e) => {
      // Ignore scroll events originating from within the dropdown itself
      if (dropdownRef.current && (dropdownRef.current === e.target || dropdownRef.current.contains(e.target))) return;
      
      setIsOpen(false);
      setSearchTerm("");
      inputRef.current?.blur();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  const handleTriggerClick = () => {
    if (disabled) return;

    if (isOpen) {
      setIsOpen(false);
      setSearchTerm("");
    } else if (inputRef.current) {
      const wrapper = inputRef.current.closest('.trigger-wrapper');
      updatePosition();
      setIsOpen(true);
      setTimeout(() => {
        inputRef.current?.focus();
        setTimeout(() => {
          if (window.innerWidth < 768) {
            wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300); // delay to wait for keyboard
      }, 0);
    }
  };

  const selectedArray = useMemo(() => {
    if (isMulti) {
      return (Array.isArray(value) ? value : []).map(String);
    } else {
      return value !== undefined && value !== null && value !== "" ? [String(value)] : [];
    }
  }, [value, isMulti]);

  // option.designation may be a single string (legacy) or an array of designation
  // objects/strings (multi-designation) — normalize to a list of display labels either way.
  const getDesignationLabels = (designation) => {
    if (!designation) return [];
    
    let rawLabels = [];
    if (Array.isArray(designation)) {
      rawLabels = designation.map(d => {
        if (typeof d === 'string') return d;
        return d?.DesignationName || d?.designationName || d?.Name || d?.name || d?.RoleName || d?.Role || '';
      });
    } else {
      rawLabels = [String(designation)];
    }

    const processedLabels = [];
    rawLabels.forEach(label => {
      if (typeof label === 'string' && label.trim()) {
        const parts = label.split(',').map(s => s.trim()).filter(Boolean);
        processedLabels.push(...parts);
      }
    });

    return Array.from(new Set(processedLabels));
  };

  const allOptions = useMemo(() => {
    const normalized = options.map(opt => {
      if (typeof opt === 'string' || typeof opt === 'number') {
        return { value: String(opt), label: String(opt) };
      }
      return opt;
    });

    const optionsMap = new Map(normalized.map(opt => [String(opt?.value), opt]));
    const combined = [...normalized];

    if (allowCustom) {
      selectedArray.forEach(val => {
        const strVal = String(val);
        if (!optionsMap.has(strVal)) {
          combined.push({ value: strVal, label: strVal });
        }
      });
    }

    // Sort to ensure selected items are at top only if sortBySelected is true
    if (sortBySelected) {
      combined.sort((a, b) => {
        const aSelected = selectedArray.includes(String(a?.value));
        const bSelected = selectedArray.includes(String(b?.value));
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0; // maintain original order for rest
      });
    }

    return combined;
  }, [options, selectedArray, allowCustom, sortBySelected]);

  const findMatchingOption = (val, opts = allOptions) => {
    if (val === undefined || val === null || val === "") return null;
    const strVal = String(val);

    // 1. Exact case-sensitive match (crucial for custom text names where there are no IDs)
    const exact = opts.find(opt => {
      if (opt === undefined || opt === null) return false;
      if (typeof opt === 'string' || typeof opt === 'number') {
        return String(opt) === strVal;
      }
      const optVal = opt.value !== undefined ? opt.value : opt.label;
      const optLabel = opt.label !== undefined ? opt.label : opt.value;
      return String(optVal) === strVal || String(optLabel) === strVal;
    });
    if (exact) return exact;

    // 2. Case-insensitive fallback for UUIDs / IDs
    const lowerVal = strVal.toLowerCase();
    const caseInsensitive = opts.find(opt => {
      if (opt === undefined || opt === null) return false;
      if (typeof opt === 'string' || typeof opt === 'number') {
        return String(opt).toLowerCase() === lowerVal;
      }
      const optVal = opt.value !== undefined ? opt.value : opt.label;
      const optLabel = opt.label !== undefined ? opt.label : opt.value;
      return (
        (optVal !== undefined && optVal !== null && String(optVal).toLowerCase() === lowerVal) ||
        (optLabel !== undefined && optLabel !== null && String(optLabel).toLowerCase() === lowerVal)
      );
    });
    if (caseInsensitive) return caseInsensitive;

    // 3. Fallback for custom text names where there are no IDs
    if (allowCustom) {
      return { value: strVal, label: strVal };
    }

    return null;
  };

  const isOptionSelected = (optValue) => {
    if (optValue === undefined || optValue === null || optValue === "") return false;
    const strOpt = String(optValue);
    return selectedArray.some(val => val !== undefined && val !== null && String(val) === strOpt);
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return allOptions;
    const lowerSearch = searchTerm.toLowerCase().trim();
    if (!lowerSearch) return allOptions;

    return allOptions.filter(opt => {
      if (!opt) return false;
      const labelStr = opt.label !== undefined && opt.label !== null ? String(opt.label).toLowerCase() : '';
      const valueStr = opt.value !== undefined && opt.value !== null ? String(opt.value).toLowerCase() : '';
      const emailStr = opt.email !== undefined && opt.email !== null ? String(opt.email).toLowerCase() : '';
      const designationStr = getDesignationLabels(opt.designation).join(' ').toLowerCase();
      const searchStringStr = opt.searchString !== undefined && opt.searchString !== null ? String(opt.searchString).toLowerCase() : '';

      return (
        labelStr.includes(lowerSearch) ||
        valueStr.includes(lowerSearch) ||
        emailStr.includes(lowerSearch) ||
        designationStr.includes(lowerSearch) ||
        searchStringStr.includes(lowerSearch)
      );
    });
  }, [allOptions, searchTerm]);

  const handleToggleOption = (optionValue) => {
    const matchedOpt = findMatchingOption(optionValue);
    const targetVal = matchedOpt ? matchedOpt.value : optionValue;

    if (isMulti) {
      const targetStr = String(targetVal);
      const index = selectedArray.findIndex(val => String(val) === targetStr);
      const newValue = [...selectedArray];

      if (index === -1) {
        newValue.push(targetVal);
      } else {
        newValue.splice(index, 1);
      }
      onChange(newValue);
      if (closeOnSelect) {
        setIsOpen(false);
        setSearchTerm("");
        inputRef.current?.blur();
      }
    } else {
      onChange(targetVal);
      setIsOpen(false);
      setSearchTerm("");
      inputRef.current?.blur();
    }
  };

  const handleSelectAll = () => {
    if (!isMulti) return;
    const allValues = options.map(opt => opt.value);
    onChange(selectedArray.length === options.length ? [] : allValues);
  };

  const getValidSelected = () => {
    return selectedArray.filter((val) => findMatchingOption(val) !== null);
  };

  const getDisplayText = () => {
    if (selectedArray.length === 0) return "";

    const validMatches = selectedArray
      .map(val => findMatchingOption(val))
      .filter(Boolean);

    if (validMatches.length === 0) return "";

    if (isMulti) {
      if (validMatches.length === 1) {
        return validMatches[0].label || "";
      }
      return `${validMatches.length} selected`;
    } else {
      return validMatches[0].label || "";
    }
  };

  const handleAddCustom = () => {
    const trimmed = customValue.trim();
    if (!trimmed) return;
    if (isMulti) {
      if (!selectedArray.includes(trimmed)) {
        onChange([...selectedArray, trimmed]);
      }
    } else {
      onChange(trimmed);
      setIsOpen(false);
      setSearchTerm("");
    }
    setCustomValue("");
  };

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={clsx(
        "z-[10050] bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-xl shadow-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200",
        usePortal ? "fixed" : "absolute top-full left-0 right-0 mt-2"
      )}
      style={usePortal && dropdownRect ? {
        ...(dropdownRect.top !== undefined ? { top: dropdownRect.top } : {}),
        ...(dropdownRect.bottom !== undefined ? { bottom: dropdownRect.bottom } : {}),
        ...(dropdownRect.left !== undefined ? { left: dropdownRect.left } : {}),
        ...(dropdownRect.right !== undefined ? { right: dropdownRect.right } : {}),
        width: Math.max(dropdownRect.width, 280),
      } : {}}
    >
      {/* Options List */}
      <div className="max-h-[220px] overflow-y-auto overflow-x-hidden p-1 custom-scrollbar">
        {(isMulti && options.length > 0 && !searchTerm) || dropdownHeader ? (
          <div className="flex items-center justify-between mb-1">
            {isMulti && options.length > 0 && !searchTerm && (
              <button
                type="button"
                onClick={handleSelectAll}
                className="flex items-center flex-1 px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/10 rounded-sm transition-colors font-medium"
              >
                <div className="mr-3 flex items-center justify-center">
                  <Checkbox
                    checked={selectedArray.length === options.length && options.length > 0}
                    indeterminate={selectedArray.length > 0 && selectedArray.length < options.length}
                    readOnly
                  />
                </div>
                {selectedArray.length === options.length ? "Deselect All" : "Select All"}
              </button>
            )}
            {dropdownHeader && (
              <div className="px-3 flex items-center">
                {dropdownHeader}
              </div>
            )}
          </div>
        ) : null}

        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const isSelected = isOptionSelected(option.value);
            return (
              <div
                key={option.value}
                onClick={() => handleToggleOption(option.value)}
                className={clsx(
                  "flex items-center px-3 py-2.5 cursor-pointer rounded-lg transition-all group",
                  isSelected
                    ? "bg-primary-50/50 dark:bg-primary-900/5"
                    : "hover:bg-gray-100 dark:hover:bg-dark-700"
                )}
              >
                {isMulti && (
                  <div className="mr-3">
                    <Checkbox checked={isSelected} readOnly />
                  </div>
                )}
                <div className="flex items-center justify-between min-w-0 flex-1 gap-2">
                  <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                    {/* Name row: full name + first designation (truncated) + extra count — all with shared tooltip */}
                    {(() => {
                      const designLabels = getDesignationLabels(option.designation);
                      const tooltipId = `desig-tooltip-${option.value}`;
                      const firstLabel = designLabels[0] || null;
                      const extraCount = designLabels.length - 1; // remaining after first
                      return (
                        <div className="flex items-center justify-between min-w-0 gap-2 w-full">
                          <span
                            className={clsx(
                              "text-sm leading-relaxed transition-colors truncate min-w-0",
                              isSelected ? "text-primary-700 dark:text-primary-300 font-semibold" : "text-gray-700 dark:text-dark-100"
                            )}
                            title={option.name || option.label}
                          >
                            {option.name || option.label}
                          </span>
                          {firstLabel && (
                            <div className="flex items-center gap-1 shrink-0">
                              {/* First designation pill — sized to its content (capped only as a
                                  safety net for pathologically long names) rather than a fixed
                                  72px that truncated even short labels like "Administrator" with
                                  room to spare; the name span above it (min-w-0 + truncate) is what
                                  actually gives way first if the row genuinely runs out of space. */}
                              <span
                                data-tooltip-id={tooltipId}
                                data-tooltip-html={designLabels.join("<br/>")}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                className="inline-flex items-center rounded-full bg-[#f1f5f9] dark:bg-slate-700 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-300 cursor-default select-none max-w-[180px]"
                              >
                                <span className="truncate">{firstLabel}</span>
                              </span>
                              {/* Extra count pill */}
                              {extraCount > 0 && (
                                <span
                                  data-tooltip-id={tooltipId}
                                  data-tooltip-html={designLabels.join("<br/>")}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                  className="inline-flex items-center rounded-full bg-[#f1f5f9] dark:bg-slate-700 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:text-slate-300 cursor-default select-none"
                                >
                                  +{extraCount}
                                </span>
                              )}
                              <ReactTooltip
                                id={tooltipId}
                                place="right"
                                // Without this, the tooltip renders inside the options list's own
                                // overflow-x-hidden/overflow-y-auto scroll container (see the .max-h-[220px]
                                // div below) and gets clipped at its edge instead of floating above
                                // everything at the viewport level — cutting off later designation names.
                                positionStrategy="fixed"
                                className="!z-[99999] !bg-gray-900/90 dark:!bg-gray-700/90 !backdrop-blur-sm !text-white !text-[10px] !px-2.5 !py-1.5 !rounded-md !shadow-lg"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {/* Optional secondary line under the name — e.g. a contractor's company —
                        distinct from option.email below it (an option could in principle have both).
                        Styled as the same pill as the designation badges above, per design request,
                        rather than plain text, so it reads as equally prominent. */}
                    {option.subtitle && (
                      <span className="inline-flex items-center self-start rounded-full bg-[#f1f5f9] dark:bg-slate-700 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-300 truncate max-w-full">
                        {option.subtitle}
                      </span>
                    )}
                    {option.email && (
                      <span className="text-[10px] text-gray-500 dark:text-dark-400 truncate">
                        {option.email}
                      </span>
                    )}
                  </div>
                  {option.isActive === false && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-extrabold bg-amber-50 text-amber-600 border border-amber-200/50 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/40 select-none shrink-0"
                    >
                      Setup Pending
                    </span>
                  )}
                  {option.actions && (
                    <div className="flex items-center gap-1 shrink-0 ml-1.5">
                      {typeof option.actions === 'function' ? option.actions(option) : option.actions}
                    </div>
                  )}
                </div>

              </div>
            );
          })
        ) : (
          <div className="py-3 text-center">
            <p className="text-sm text-gray-500 dark:text-dark-400">No data found matching &quot;{searchTerm}&quot;</p>
          </div>
        )}
      </div>

      {/* Custom Add Option Input (if allowCustom is true) */}
      {allowCustom && (
        <div className="p-1 border-gray-200 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-900/30 flex gap-2">
          <input
            type="text"
            placeholder="Or add more..."
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            className="text-xs flex-1 rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-900 px-2.5 py-1.5 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-900 dark:text-white placeholder:text-gray-400"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                handleAddCustom();
              }
            }}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleAddCustom();
            }}
            className="px-3 py-1.5 text-xs font-semibold bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors shadow-sm"
          >
            Add
          </button>
        </div>
      )}

      {/* Footer */}
      {isMulti && selectedArray.length > 0 && (
        <div className="p-2 border-t border-gray-100 dark:border-dark-700 bg-gray-55 dark:bg-dark-900/30 flex justify-between items-center">
          <span className="text-[10px] font-medium text-gray-500 dark:text-dark-400 ml-1">
            {selectedArray.length} selected
          </span>
          <button
            onClick={() => onChange([])}
            className="text-[10px] font-bold text-red-500 hover:text-red-600 px-2 py-1 rounded transition-colors"
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={clsx("relative w-full flex flex-col min-w-0", className)} ref={containerRef}>
      {label && (
        <label className="text-sm font-medium text-gray-700 dark:text-dark-300 mb-2 flex items-center gap-1">
          <span>{label}</span>
          {required && <span className="text-error font-bold">*</span>}
        </label>
      )}

      <div
        onClick={handleTriggerClick}
        className={clsx(
          "trigger-wrapper relative w-full flex items-center justify-between bg-white dark:bg-dark-800 border rounded-lg transition-all duration-200",
          size === "sm" ? "h-9 px-3 py-1" : "px-4 py-2",
          isOpen
            ? (error ? "border-error ring-4 ring-error/10 cursor-text" : "border-primary-500 ring-4 ring-primary-500/10 cursor-text")
            : (error ? "border-error dark:border-error-lighter cursor-pointer" : "border-gray-300 dark:border-dark-600 hover:border-gray-400 dark:hover:border-dark-500 cursor-pointer"),
          disabled && "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-dark-900"
        )}
      >
        <div className={clsx("flex items-center gap-2 min-w-0 flex-1 relative", size === "sm" ? "h-5" : "h-6")}>
          <input
            ref={inputRef}
            type="text"
            className={clsx(
              "absolute inset-0 w-full h-full bg-transparent border-none outline-none p-0 text-sm focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-dark-300",
              isOpen ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none",
              size === "sm" && "text-xs"
            )}
            placeholder={selectedArray.length > 0 ? getDisplayText() : placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => {
              e.stopPropagation();
              if (!isOpen) {
                handleTriggerClick();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (isOpen && filteredOptions.length > 0) {
                  handleToggleOption(filteredOptions[0].value);
                }
              }
            }}
            disabled={disabled}
          />
          <span className={clsx(
            "truncate text-sm select-none pointer-events-none",
            isOpen ? "invisible" : (getValidSelected().length > 0 ? "text-gray-900 dark:text-dark-100 font-medium" : "text-gray-500 dark:text-dark-400"),
            size === "sm" && "text-xs"
          )}>
            {getValidSelected().length > 0 ? getDisplayText() : placeholder}
          </span>
        </div>

        <div className="flex items-center ml-2 border-l border-gray-200 dark:border-dark-700 pl-2 gap-1 cursor-pointer">
          {!disabled && selectedArray.length > 0 && (
            <XMarkIcon
              className="h-4 w-4 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onChange(isMulti ? [] : "");
              }}
            />
          )}
          <ChevronDownIcon className={clsx(
            "h-5 w-5 text-gray-400 transition-transform duration-200 cursor-pointer",
            isOpen && "rotate-180"
          )} />
        </div>
      </div>

      {isOpen && (
        usePortal ? createPortal(dropdownContent, document.body) : dropdownContent
      )}
      {error && (
        <span className="input-text-error mt-1 text-xs text-error dark:text-error-lighter">
          {error}
        </span>
      )}
    </div>
  );
};
